import type {ReactNode} from 'react'
import {PullRequestState} from '../../../../types'
import {ClosedPR, shouldShowClosedPR} from '../pull-request-state/ClosedPR'
import {MergedPR, shouldShowMergedPR} from '../pull-request-state/MergedPR'
import {AbleToMerge, shouldShowAbleToMerge} from '../mergeability/AbleToMerge'
import {ApprovableChecks} from '../mergeability/ApprovableChecks'
import {AwaitingApprovalChecks} from '../mergeability/AwaitingApprovalChecks'
import {shouldShowDraftPR, DraftPR} from '../pull-request-state/DraftPR'
import {FailingChecks} from '../mergeability/FailingChecks'
import {
  shouldShowBlockedByDownstackPullRequest,
  BlockedByDownstackPullRequest,
} from '../mergeability/BlockedByDownstackPullRequest'
import {shouldShowMergeBlocked, MergeBlocked} from '../mergeability/MergeBlocked'
import {shouldShowMergeConflicts, MergeConflicts} from '../mergeability/MergeConflicts'
import {PendingChecks} from '../mergeability/PendingChecks'
import {shouldShowReadyToMerge, ReadyToMerge} from '../mergeability/ReadyToMerge'
import {ReviewSubmitted} from '../mergeability/ReviewSubmitted'
import {getBranchUpdateRequirement, UpdateBranch} from '../mergeability/UpdateBranch'
import {decideChecksItem} from '../mergeability/shared/checks-item-decider'
import {decideReviewSubmittedItem} from '../mergeability/shared/review-submitted-item-decider'
import {selectFailedRequiredChecks} from '../mergeability/use-failed-checks-data'
import {useMergeabilityData, type MergeabilityData} from '../mergeability/use-mergeability-data'
import {usePullRequestState} from '../../../../hooks/use-pull-request-state'
import {useUnresolvedConversationCount} from '../../../../stores/layout-selectors'
import {QueuedPR, shouldShowQueuedPR} from '../pull-request-state/QueuedPR'
import {StackClosed, shouldShowStackClosed} from '../stack/StackClosed'
import {useHeaderPageData} from '../../../../page-data/loaders/use-header-page-data'
import type {StackData} from '../../../../page-data/payloads/header'

type AuthorForYouActionInput = {
  mergeabilityData: MergeabilityData | null
  pullRequestState: PullRequestState
  pullRequestNumber?: number
  stack?: StackData | null
  hasUnresolvedThreads?: boolean
}

type LazyAction = {
  action: ReactNode
  matches: () => boolean
}

export function useAuthorOrAssigneeDecider() {
  const mergeabilityData = useMergeabilityData()
  const pullRequestState = usePullRequestState()
  const unresolvedThreadCount = useUnresolvedConversationCount() ?? 0
  const {pullRequest, stack} = useHeaderPageData().data

  return getAuthorForYouAction({
    mergeabilityData,
    pullRequestState,
    pullRequestNumber: pullRequest.number,
    stack,
    hasUnresolvedThreads: unresolvedThreadCount > 0,
  })
}

export function getAuthorForYouAction({
  mergeabilityData,
  pullRequestState,
  pullRequestNumber = 0,
  stack = null,
  hasUnresolvedThreads = false,
}: AuthorForYouActionInput): ReactNode {
  const decisionTree: LazyAction[] = [
    {action: <ClosedPR variant="embedded" userRole="author" />, matches: () => shouldShowClosedPR(pullRequestState)},
    {
      action: <MergedPR variant="embedded" userRole="author" />,
      matches: () => shouldShowMergedPR(pullRequestState),
    },
    {
      action: <QueuedPR variant="embedded" />,
      matches: () => shouldShowQueuedPR(mergeabilityData),
    },
    {action: <MergeConflicts variant="embedded" />, matches: () => shouldShowMergeConflicts(mergeabilityData)},
    {
      action: <StackClosed />,
      matches: () => shouldShowStackClosed(stack, pullRequestNumber),
    },
    {
      action: <FailingChecks />,
      matches: () => Boolean(mergeabilityData && selectFailedRequiredChecks(mergeabilityData.statusChecks).length),
    },
    {
      action: <ApprovableChecks variant="embedded" />,
      matches: () => decideChecksItem(mergeabilityData).kind === 'approvable',
    },
    {
      action: <AwaitingApprovalChecks variant="embedded" />,
      matches: () => decideChecksItem(mergeabilityData).kind === 'awaiting-approval',
    },
    {
      action: <PendingChecks variant="embedded" />,
      matches: () => decideChecksItem(mergeabilityData).kind === 'pending',
    },
    {action: <DraftPR userRole="author" />, matches: () => shouldShowDraftPR(pullRequestState)},
    {
      action: <ReviewSubmitted />,
      matches: () => decideReviewSubmittedItem(mergeabilityData, hasUnresolvedThreads).kind !== 'none',
    },
    {
      action: <UpdateBranch variant="embedded" />,
      matches: () => getBranchUpdateRequirement(mergeabilityData) === 'required',
    },
    {
      action: <BlockedByDownstackPullRequest />,
      matches: () => shouldShowBlockedByDownstackPullRequest(mergeabilityData),
    },
    {
      action: <UpdateBranch variant="embedded" />,
      matches: () => getBranchUpdateRequirement(mergeabilityData) === 'optional',
    },
    {action: <MergeBlocked />, matches: () => shouldShowMergeBlocked(mergeabilityData)},
    {action: <ReadyToMerge />, matches: () => shouldShowReadyToMerge(mergeabilityData)},
    {action: <AbleToMerge />, matches: () => shouldShowAbleToMerge(mergeabilityData)},
  ]

  return decisionTree.find(decision => decision.matches())?.action ?? null
}
